// frontend/src/components/Layout.js
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, clearTokens, getAccessToken } from "../api/client";
import Sidebar from "./Sidebar";
import Footer from "./Footer";

/* -------------------------- Tiny mobile breakpoint -------------------------- */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

/* ---------- Tiny Error Boundary so pages never render blank ---------- */
function ErrorBoundary({ children }) {
  const [err, setErr] = useState(null);
  if (err) {
    return (
      <div style={baseStyles.centerWrap}>
        <div style={baseStyles.card}>
          <h2 style={baseStyles.h2}>Something went wrong</h2>
          <div style={{ color: "#b00020" }}>{String(err)}</div>
          <button style={baseStyles.btn} onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }
  return <ErrorCatcher onError={setErr}>{children}</ErrorCatcher>;
}
class ErrorCatcher extends React.Component {
  componentDidCatch(error) {
    this.props.onError(error);
  }
  render() {
    return this.props.children;
  }
}

/* -------------------------- Global CSS injection -------------------------- */
/**
 * We scope zebra striping specifically to the MyPoints table via its aria-label.
 * Target the <td> cells for maximal reliability across browsers / default styles.
 */
function GlobalStyles() {
  return (
    <style>{`
      /* Zebra striping for MyPoints */
      table[aria-label="My points history"] tbody tr:nth-child(odd) td {
        background: #fbfdff;
      }
      /* Optional: highlight on hover without clashing with striping */
      table[aria-label="My points history"] tbody tr:hover td {
        background: #f3f8ff;
      }
    `}</style>
  );
}

/* ------------------------------- Layout ------------------------------- */
export default function Layout({ children }) {
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  const navigate = useNavigate();
  const location = useLocation();

  // Keep sidebar default in sync with viewport changes
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);

      // If no token, send to login
      const token = getAccessToken?.();
      if (!token) {
        navigate("/login", { replace: true, state: { from: location } });
        return;
      }

      try {
        // ✅ Django endpoints expect trailing slash
        const res = await api.get("/api/auth/me/");
        if (!mounted) return;

        if (res?.ok) {
          // Normalize case on email/username so domain checks are reliable
          const raw = res.data || {};
          const email = (raw.email || "").toLowerCase();
          const username = (raw.username || "").toLowerCase();
          const normalized = { ...raw, email, username };

          const isStudentDomain =
            /@student\.propelschools\.org$/i.test(email) ||
            /@student\.propelschools\.org$/i.test(username);
          // eslint-disable-next-line no-console
          console.debug("[me]", {
            role: normalized.role,
            email: normalized.email,
            username: normalized.username,
            isStudentDomain,
          });

          setMe(normalized);
        } else {
          // token expired / invalid
          clearTokens?.();
          navigate("/login", { replace: true, state: { from: location } });
          return;
        }
      } catch {
        // Network/500 — treat as auth failure to avoid a blank page
        clearTokens?.();
        navigate("/login", { replace: true, state: { from: location } });
        return;
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSignOut() {
    try {
      clearTokens?.();
    } catch {}
    navigate("/login", { replace: true });
  }

  if (loading) {
    return (
      <div style={baseStyles.centerWrap}>
        <div style={baseStyles.card}>
          <div>Loading…</div>
        </div>
      </div>
    );
  }

  /* ------------------------ Responsive inline styles ------------------------ */
  const s = computeStyles({ isMobile, sidebarOpen });

  return (
    <div style={s.appShell}>
      {/* Small global stylesheet for targeted tweaks */}
      <GlobalStyles />

      {/* Sidebar (desktop = sticky column; mobile = off-canvas drawer) */}
      <aside
        style={s.sidebar}
        role={isMobile ? "dialog" : "complementary"}
        aria-modal={isMobile || undefined}
        aria-hidden={isMobile ? (!sidebarOpen).toString() : undefined}
      >
        {!isMobile && (
          <div style={s.brandRow}>
            <button
              aria-label="Toggle sidebar"
              onClick={() => setSidebarOpen((v) => !v)}
              style={s.iconBtn}
              title={sidebarOpen ? "Collapse" : "Expand"}
            >
              ☰
            </button>
            {sidebarOpen && <div style={s.brand}>Propel Points</div>}
          </div>
        )}

        <Sidebar
          me={me}
          open={sidebarOpen}
          onSignOut={handleSignOut}
          onClose={() => isMobile && setSidebarOpen(false)}
        />
      </aside>

      {/* Tap-to-close overlay for mobile drawer */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={s.overlay}
          aria-hidden="true"
        />
      )}

      {/* Main column */}
      <div style={s.mainCol}>
        <header style={s.topbar}>
          {isMobile && (
            <button
              aria-label="Open menu"
              onClick={() => setSidebarOpen(true)}
              style={s.iconBtn}
              title="Open menu"
            >
              ☰
            </button>
          )}
          {isMobile && <div style={s.topbarTitle}>Propel Points</div>}
        </header>

        <main style={s.mainArea}>
          <ErrorBoundary>{children}</ErrorBoundary>
        </main>

        <Footer />
      </div>
    </div>
  );
}

/* ------------------------------ Style system ------------------------------ */
const baseStyles = {
  centerWrap: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    background: "#F7F9FC",
    padding: 24,
  },
  card: {
    width: 460,
    maxWidth: "92vw",
    background: "#fff",
    padding: 16,
    borderRadius: 16,
    boxShadow: "0 10px 24px rgba(0,0,0,0.08)",
    display: "grid",
    gap: 12,
  },
  h2: { margin: 0, fontSize: 20, color: "#003e73", fontWeight: 800 },
  btn: {
    height: 40,
    borderRadius: 999,
    border: "none",
    background: "#f9a21a",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },
};

function computeStyles({ isMobile, sidebarOpen }) {
  const sidebarWidthDesktop = sidebarOpen ? 240 : 68;

  return {
    appShell: {
      minHeight: "100vh",
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "auto 1fr",
      background: "#F7F9FC",
    },

    sidebar: {
      background: "#ffffff",
      borderRight: "1px solid rgba(0,0,0,0.08)",
      display: "grid",
      gridTemplateRows: isMobile ? "1fr" : "56px 1fr",
      transition: isMobile ? "transform 0.2s ease" : "width 0.18s ease",
      overflowX: "hidden",

      position: isMobile ? "fixed" : "sticky",
      top: 0,
      alignSelf: "start",
      height: "100vh",
      overflowY: "auto",

      width: isMobile ? 260 : sidebarWidthDesktop,

      left: 0,
      transform: isMobile ? `translateX(${sidebarOpen ? "0" : "-100%"})` : "none",
      zIndex: 40,
    },

    brandRow: {
      height: 56,
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "0 10px",
      borderBottom: "1px solid rgba(0,0,0,0.06)",
      background: "#ffffff",
    },

    iconBtn: {
      width: 36,
      height: 36,
      borderRadius: 10,
      border: "1px solid #dae7f2",
      background: "#fff",
      color: "#003e73",
      fontWeight: 900,
      cursor: "pointer",
    },

    brand: { fontWeight: 900, color: "#003e73" },

    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.32)",
      zIndex: 30,
    },

    mainCol: {
      display: "grid",
      gridTemplateRows: "56px 1fr auto",
      minWidth: 0,
      minHeight: "100vh",
      overflow: "auto",
      background: "transparent",
    },

    topbar: {
      height: 56,
      background: "#ffffff",
      borderBottom: "1px solid rgba(0,0,0,0.08)",
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "0 12px",
      position: isMobile ? "sticky" : "relative",
      top: 0,
      zIndex: 10,
    },

    topbarTitle: {
      fontWeight: 900,
      color: "#003e73",
      marginLeft: 4,
    },

    mainArea: {
      padding: isMobile ? 12 : 16,
    },
  };
}
